import {
  Children,
  Fragment,
  isValidElement,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react'
import { createPortal } from 'react-dom'
import { Symbol } from './Symbol'

interface Auswahloption {
  value: string
  inhalt: ReactNode
  text: string
  disabled: boolean
}

function textAus(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(textAus).join('')
  if (isValidElement<{ children?: ReactNode }>(node)) return textAus(node.props.children)
  return ''
}

function optionenAus(children: ReactNode): Auswahloption[] {
  const optionen: Auswahloption[] = []
  const besuchen = (knoten: ReactNode) => {
    Children.forEach(knoten, (kind) => {
      if (
        !isValidElement<{ value?: string | number; disabled?: boolean; children?: ReactNode }>(kind)
      )
        return
      if (kind.type === Fragment) {
        besuchen(kind.props.children)
        return
      }
      if (kind.type !== 'option') return
      const inhalt = kind.props.children
      optionen.push({
        value: String(kind.props.value ?? ''),
        inhalt,
        text: textAus(inhalt),
        disabled: Boolean(kind.props.disabled),
      })
    })
  }
  besuchen(children)
  return optionen
}

const naechsterIndex = (optionen: Auswahloption[], von: number, richtung: 1 | -1): number => {
  if (!optionen.length) return -1
  for (let schritt = 1; schritt <= optionen.length; schritt++) {
    const index = (von + schritt * richtung + optionen.length) % optionen.length
    if (!optionen[index]?.disabled) return index
  }
  return -1
}

/** Eigene Einzelauswahl; das native Feld bleibt nur als Formularzustand erhalten. */
export function Select({
  id,
  label,
  wert,
  onWert,
  hilfe,
  fehler,
  className,
  children,
  disabled = false,
  required = false,
  ...rest
}: {
  id: string
  label: string
  wert: string
  onWert: (wert: string) => void
  hilfe?: string
  fehler?: string
  children: ReactNode
} & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'value' | 'onChange' | 'children'>) {
  const reactId = useId().replaceAll(':', '')
  const listboxId = `${id}-liste-${reactId}`
  const labelId = `${id}-label`
  const knopf = useRef<HTMLButtonElement>(null)
  const liste = useRef<HTMLDivElement>(null)
  const [offen, setOffen] = useState(false)
  const [aktiv, setAktiv] = useState(-1)
  const [imDialog, setImDialog] = useState(false)
  const [position, setPosition] = useState({ links: 0, oben: 0, breite: 0, maxHoehe: 320 })
  const optionen = optionenAus(children)
  const gewaehlt = optionen.findIndex((option) => option.value === wert)
  const hinweis = fehler ?? hilfe

  const positionieren = () => {
    const rechteck = knopf.current?.getBoundingClientRect()
    if (!rechteck) return
    const dialog = knopf.current?.closest('dialog')
    const dialogRechteck = dialog?.getBoundingClientRect()
    const rand = 8
    const grenzeOben = Math.max(rand, dialogRechteck?.top ?? rand)
    const grenzeUnten = Math.min(
      window.innerHeight - rand,
      dialogRechteck?.bottom ?? window.innerHeight - rand,
    )
    const unten = grenzeUnten - rechteck.bottom
    const oben = rechteck.top - grenzeOben
    const maxHoehe = Math.max(120, Math.min(320, Math.max(unten, oben)))
    const klapptNachOben = unten < 200 && oben > unten
    const erwarteteHoehe = Math.min(maxHoehe, optionen.length * 48 + 16)
    const grenzeLinks = Math.max(rand, dialogRechteck?.left ?? rand)
    const grenzeRechts = Math.min(
      window.innerWidth - rand,
      dialogRechteck?.right ?? window.innerWidth - rand,
    )
    const breite = Math.min(Math.max(rechteck.width, 180), grenzeRechts - grenzeLinks)
    const linksViewport = Math.min(Math.max(grenzeLinks, rechteck.left), grenzeRechts - breite)
    const obenViewport = klapptNachOben
      ? Math.max(grenzeOben, rechteck.top - erwarteteHoehe - 4)
      : Math.min(grenzeUnten - erwarteteHoehe, rechteck.bottom + 4)
    setImDialog(Boolean(dialogRechteck))
    setPosition({
      links: linksViewport - (dialogRechteck?.left ?? 0),
      oben: obenViewport - (dialogRechteck?.top ?? 0),
      breite,
      maxHoehe,
    })
  }

  const oeffnen = (start = gewaehlt) => {
    if (disabled) return
    const gueltigerStart =
      start >= 0 && !optionen[start]?.disabled ? start : naechsterIndex(optionen, -1, 1)
    setAktiv(gueltigerStart)
    setOffen(true)
  }

  const schliessen = (fokus = false) => {
    setOffen(false)
    if (fokus) requestAnimationFrame(() => knopf.current?.focus())
  }

  const auswaehlen = (index: number) => {
    const option = optionen[index]
    if (!option || option.disabled) return
    onWert(option.value)
    schliessen(true)
  }

  useLayoutEffect(() => {
    if (offen) positionieren()
  }, [offen])

  // ⚠️ Die Liste ist bei vielen Einträgen gedeckelt und scrollt. Die Pfeiltasten
  // verschieben nur `aktiv` - ohne diesen Schritt wandert die Markierung aus
  // dem sichtbaren Bereich und man bedient eine Liste, die man nicht sieht
  // (gemessen: Ende einer scrollenden Liste 91 px unterhalb des Rands).
  // Gerechnet statt `scrollIntoView`: das scrollt auch Vorfahren mit, und die
  // Liste liegt im geöffneten Blatt - dort würde das den Dialog verschieben.
  useLayoutEffect(() => {
    const kasten = liste.current
    if (!offen || !kasten || aktiv < 0) return
    const option = kasten.children[aktiv]
    if (!(option instanceof HTMLElement)) return
    const rand = parseFloat(getComputedStyle(kasten).paddingTop) || 0
    const oben = option.offsetTop
    const unten = oben + option.offsetHeight
    if (oben - rand < kasten.scrollTop) kasten.scrollTop = Math.max(0, oben - rand)
    else if (unten + rand > kasten.scrollTop + kasten.clientHeight)
      kasten.scrollTop = unten + rand - kasten.clientHeight
  }, [offen, aktiv])

  useEffect(() => {
    if (!offen) return
    const aussen = (event: PointerEvent) => {
      const ziel = event.target
      if (!(ziel instanceof Node) || knopf.current?.contains(ziel)) return
      const liste = document.getElementById(listboxId)
      if (!liste?.contains(ziel)) schliessen()
    }
    const neuPositionieren = () => positionieren()
    document.addEventListener('pointerdown', aussen)
    window.addEventListener('resize', neuPositionieren)
    window.addEventListener('scroll', neuPositionieren, true)
    return () => {
      document.removeEventListener('pointerdown', aussen)
      window.removeEventListener('resize', neuPositionieren)
      window.removeEventListener('scroll', neuPositionieren, true)
    }
  }, [offen, listboxId])

  const tastatur = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!offen) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault()
        if (event.key === 'End') oeffnen(naechsterIndex(optionen, 0, -1))
        else if (event.key === 'Home') oeffnen(naechsterIndex(optionen, -1, 1))
        else if (event.key === 'ArrowUp')
          oeffnen(naechsterIndex(optionen, gewaehlt < 0 ? 0 : gewaehlt, -1))
        else oeffnen()
      }
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      schliessen(true)
    } else if (event.key === 'Tab') {
      schliessen()
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      setAktiv((alt) => naechsterIndex(optionen, alt, event.key === 'ArrowDown' ? 1 : -1))
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault()
      setAktiv(
        event.key === 'Home' ? naechsterIndex(optionen, -1, 1) : naechsterIndex(optionen, 0, -1),
      )
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      auswaehlen(aktiv)
    } else if (event.key.length === 1) {
      const suche = event.key.toLocaleLowerCase('de-DE')
      const passt = (option: Auswahloption) =>
        !option.disabled && option.text.toLocaleLowerCase('de-DE').startsWith(suche)
      const treffer = optionen.findIndex((option, index) => index > aktiv && passt(option))
      const vonVorn = optionen.findIndex(passt)
      if (treffer >= 0 || vonVorn >= 0) setAktiv(treffer >= 0 ? treffer : vonVorn)
    }
  }

  const ausgewaehlt = optionen[gewaehlt]
  const popupStyle = {
    '--auswahl-links': `${position.links}px`,
    '--auswahl-oben': `${position.oben}px`,
    '--auswahl-breite': `${position.breite}px`,
    '--auswahl-maxhoehe': `${position.maxHoehe}px`,
  } as CSSProperties

  return (
    <div className={`tf tf--wahl${fehler ? ' tf--fehler' : ''}${className ? ` ${className}` : ''}`}>
      <select
        id={id}
        className="tf__nativ-zustand"
        value={wert}
        onChange={(event) => onWert(event.target.value)}
        disabled={disabled}
        required={required}
        tabIndex={-1}
        aria-hidden="true"
        {...rest}
      >
        {children}
      </select>
      <button
        ref={knopf}
        id={`${id}-auswahl`}
        type="button"
        className="tf__eingabe tf__wahlknopf"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={offen}
        aria-controls={listboxId}
        aria-labelledby={labelId}
        aria-activedescendant={offen && aktiv >= 0 ? `${listboxId}-option-${aktiv}` : undefined}
        aria-describedby={hinweis ? `${id}-hinweis` : undefined}
        aria-invalid={fehler ? true : undefined}
        aria-required={required || undefined}
        disabled={disabled}
        onClick={() => (offen ? schliessen() : oeffnen())}
        onKeyDown={tastatur}
      >
        <span className="tf__wahlwert">{ausgewaehlt?.inhalt ?? 'Bitte auswählen'}</span>
        <Symbol name="chevron-down" className="tf__pfeil" />
      </button>
      <label className="tf__label tf__label--fest" id={labelId} htmlFor={`${id}-auswahl`}>
        {label}
      </label>
      {hinweis ? (
        <p className="tf__hinweis" id={`${id}-hinweis`} {...(fehler ? { role: 'alert' } : {})}>
          {hinweis}
        </p>
      ) : null}
      {offen && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={liste}
              id={listboxId}
              className={`auswahlliste${imDialog ? ' auswahlliste--im-dialog' : ''}`}
              role="listbox"
              aria-labelledby={labelId}
              style={popupStyle}
            >
              {optionen.map((option, index) => (
                <div
                  key={`${option.value}-${index}`}
                  id={`${listboxId}-option-${index}`}
                  className={`auswahloption${index === aktiv ? ' auswahloption--aktiv' : ''}`}
                  role="option"
                  data-value={option.value}
                  aria-selected={option.value === wert}
                  aria-disabled={option.disabled || undefined}
                  onPointerDown={(event) => event.preventDefault()}
                  onClick={() => auswaehlen(index)}
                >
                  <span>{option.inhalt}</span>
                  {option.value === wert ? <Symbol name="check" /> : null}
                </div>
              ))}
            </div>,
            knopf.current?.closest('dialog') ?? document.body,
          )
        : null}
    </div>
  )
}
